import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import _ from 'lodash';
import shortid from 'shortid';
import { change } from 'redux-form';

import { uploading } from 'actions/statusActions';
import {
  changeActivityData,
  saveEditingActivity,
} from 'actions/flinkMakeActions';
import { PreviewImage, AddImageDialog } from 'components/flink-components';
import RadioGroup from 'components/flink-components/ActivityOptions/RadioGroup';
import { imagesURL, uploadsURL, activitiesKey } from 'config';
import API from 'api';

import classes from './AddImageBlock.module.scss';

class AddImageBlock extends Component {
  state = {
    showPreview: false,
    showPick: false,
  };

  closeModals = () => {
    if (this.state.showPreview || this.state.showPick) {
      this.setState({ showPreview: false, showPick: false });
    }
  };

  getPath = () =>{
    const { versionType, wordAgs, filePath } = this.props;

    if (!wordAgs) {
      if (versionType)
        return `${activitiesKey}/${this.props.editingActivity.activity._id}/languageVersionData/${versionType}`;
      else
        return `${activitiesKey}/${this.props.editingActivity.activity._id}/gamedata`;
    }
    else{
      return filePath;
    }
  }

  getCurrent = () => {
    const { editingActivity, pathToProp, wordAgs, fileName } = this.props;

    if (!wordAgs)
      return _.get(editingActivity.data, pathToProp);
    else
      return typeof fileName === 'string' ? fileName : '';
  };

  deleteHandler = async () => {
    const path = this.getPath();
    const current = this.getCurrent();
    const activityId = this.props.editingActivity?.activity?._id || '';

    if (!current) {
      this.closeModals();
      return true;
    }

    console.log('[EBOOK_MEDIA_DIAG][FRONT][DELETE_START]', {
      activityId,
      versionType: this.props.versionType || '',
      path,
      current,
      key: `${path}/${current}`,
      pathToProp: this.props.pathToProp,
    });

    // First persist removal of the reference. Only after that succeeds is it safe
    // to remove the physical object from S3.
    const saved = await this.saveChanges(null);
    if (saved === false) {
      console.error('[EBOOK_MEDIA_DIAG][FRONT][DELETE_SAVE_FAILED]', {
        activityId,
        versionType: this.props.versionType || '',
        pathToProp: this.props.pathToProp,
        current,
      });
      return false;
    }

    this.closeModals();

    const key = `${path}/${current}`;
    const deleted = await API.storage.deleteFiles([key]);

    console.log('[EBOOK_MEDIA_DIAG][FRONT][DELETE_RESULT]', {
      activityId,
      versionType: this.props.versionType || '',
      pathToProp: this.props.pathToProp,
      key,
      deleted,
    });

    return deleted;
  };

  uploadHandler = async (file) => {
    if (!file) return null;

    const { uploading, fileName, withId, versionType, pathToProp } = this.props;
    const activityId = this.props.editingActivity?.activity?._id || '';

    const path = this.getPath();

    const [, originFileName, extension] = file.name.match(/\/?([^/]+)\.(\S+)$/);

    const fileNameWithExt = `${fileName ? fileName : originFileName}${withId ? '_' + shortid.generate() : ''
      }.${extension}`;

    const key = `${path}/${fileNameWithExt}`;

    console.log('[EBOOK_MEDIA_DIAG][FRONT][UPLOAD_START]', {
      activityId,
      versionType: versionType || '',
      pathToProp,
      path,
      key,
      generatedFilename: fileNameWithExt,
      originalFilename: file.name,
      size: file.size,
      type: file.type,
    });

    const success = await API.storage.uploadFile(key, file, {
      onUploadProgress: uploading,
    });

    uploading(null);

    console.log('[EBOOK_MEDIA_DIAG][FRONT][UPLOAD_RESULT]', {
      activityId,
      versionType: versionType || '',
      pathToProp,
      path,
      key,
      generatedFilename: fileNameWithExt,
      success,
    });

    if (!success) {
      console.error('[EBOOK_MEDIA_DIAG][FRONT][UPLOAD_FAILED]', {
        activityId,
        versionType: versionType || '',
        pathToProp,
        key,
      });
    }

    return success ? fileNameWithExt : null;
  };

  changeHandler = async ({ file }) => {
    const current = this.getCurrent();
    const activityId = this.props.editingActivity?.activity?._id || '';
    const path = this.getPath();

    console.log('[EBOOK_MEDIA_DIAG][FRONT][CHANGE_START]', {
      activityId,
      versionType: this.props.versionType || '',
      pathToProp: this.props.pathToProp,
      current,
      path,
      selectedFile: file ? { name: file.name, size: file.size, type: file.type } : null,
    });

    // Upload the replacement first. A failed upload must leave the current image
    // and its saved reference untouched.
    const filename = await this.uploadHandler(file);

    console.log('[EBOOK_MEDIA_DIAG][FRONT][CHANGE_AFTER_UPLOAD]', {
      activityId,
      versionType: this.props.versionType || '',
      pathToProp: this.props.pathToProp,
      current,
      filename,
    });

    if (!filename) {
      console.error('[EBOOK_MEDIA_DIAG][FRONT][CHANGE_UPLOAD_NULL]', {
        activityId,
        versionType: this.props.versionType || '',
        pathToProp: this.props.pathToProp,
        current,
      });
      return false;
    }

    // Replacement is atomic from the editor/data.json point of view: write and
    // persist the new filename once. Never save an intermediate null value.
    const saved = await this.saveChanges(filename);
    if (saved === false) {
      console.error('[EBOOK_MEDIA_DIAG][FRONT][CHANGE_SAVE_FAILED]', {
        activityId,
        versionType: this.props.versionType || '',
        pathToProp: this.props.pathToProp,
        current,
        filename,
      });
      return false;
    }

    this.closeModals();

    // Cleanup is deliberately last. If it fails, the only consequence is an
    // orphaned old object; the newly saved image reference remains valid.
    if (current && current !== filename) {
      const oldKey = `${path}/${current}`;
      const deleted = await API.storage.deleteFiles([oldKey]);
      console.log('[EBOOK_MEDIA_DIAG][FRONT][REPLACED_OLD_FILE_CLEANUP]', {
        activityId,
        versionType: this.props.versionType || '',
        pathToProp: this.props.pathToProp,
        oldKey,
        deleted,
      });
    }

    return true;
  };

  saveChanges = (filename) => {
    const {
      changeActivityData,
      onChangeHandler,
      saveEditingActivity,
      pathToProp,
      wordAgs,
      versionType,
    } = this.props;

    console.log('[EBOOK_MEDIA_DIAG][FRONT][SAVE_CHANGES]', {
      activityId: this.props.editingActivity?.activity?._id || '',
      versionType: versionType || '',
      pathToProp,
      filename,
      currentBeforeDispatch: this.getCurrent(),
      uploadPath: this.getPath(),
      wordAgs: !!wordAgs,
    });

    if (!wordAgs) {
      changeActivityData(pathToProp, filename);
      onChangeHandler && onChangeHandler(filename);

      const savePromise = saveEditingActivity({ source: 'AddImageBlock.saveChanges' });
      if (savePromise && typeof savePromise.then === 'function') {
        return savePromise
          .then(success => {
            console.log('[EBOOK_MEDIA_DIAG][FRONT][SAVE_RESULT]', {
              activityId: this.props.editingActivity?.activity?._id || '',
              versionType: versionType || '',
              pathToProp,
              filename,
              success,
            });
            return success;
          })
          .catch(error => {
            console.error('[EBOOK_MEDIA_DIAG][FRONT][SAVE_REJECTED]', {
              activityId: this.props.editingActivity?.activity?._id || '',
              versionType: versionType || '',
              pathToProp,
              filename,
              error,
            });
            return false;
          });
      }

      return savePromise !== false;
    }

    return onChangeHandler ? onChangeHandler(filename) : true;
  };

  showPreviewModal = (showPreview) =>
    this.setState({ showPreview, showPick: false });

  showImagePickModal = (showPick) =>
    this.setState({ showPreview: false, showPick });

  render() {
    const { showPreview, showPick } = this.state;

    const {
      maxSize,
      translate,
      showFilename,
      withOverlay,
      withSize,
      withImageDefaultOpenCheckbox,
      withTestBtn,
      withoutTitle,
      withoutPreview,
      changeActivityData,
      editingActivity,
      disabled,
      versionType,
      wordAgs,
      wordIndex,
      addImagePerks,
      exclusiveImageActions
    } = this.props;

    const path = this.getPath();
    const current = this.getCurrent();

    let currentSrc = current ? `${uploadsURL}/${path}/${current}` : null;
    console.log('AddImage currentSrc: ',[currentSrc, wordIndex, addImagePerks])
    return (
      <>
        <div className={classes.wrapper}>
          {!withoutPreview && (
            <div className={classes.imageAnswerPreview}>
              {currentSrc && <img src={currentSrc} alt="" />}
            </div>
          )}

            {((wordAgs && addImagePerks) || !wordAgs)
              && !(wordAgs && exclusiveImageActions && current) &&
            <div
              className={`${classes.addBtnWrapper} ${disabled ? classes.disabled : ''
                }`}
            >
              {!withoutTitle && (
                <span className={classes.btnTitle}>
                  {translate(17, 'Add Image')}
                </span>
              )}
              <button
                disabled={disabled}
                className={disabled ? '' : 'btn-effect'}
                title={translate(17, 'Add Image', true)}
                onClick={() => this.showImagePickModal(!showPick)}
              >
                <img src={`${imagesURL}/FrontendUtilities/addimage.png`} alt="" />
              </button>
            </div>
          }
          
          <AddImageDialog
            withOverlay={withOverlay}
            show={showPick}
            closeHandler={() => this.showImagePickModal(false)}
            onPick={this.changeHandler}
            maxSize={maxSize}
            path={path}
          />

          {current && (
            <>
              {showFilename && (
                <span className={classes['text']}>{current}</span>
              )}

              {((wordAgs && !withoutPreview && addImagePerks) || !wordAgs) && (
              <button
                disabled={disabled}
                className={`btn-effect ${classes['delete-button']}`}
                onClick={this.deleteHandler}
                title={translate(153, 'Delete Image')}
              >
                <img
                  src={`${imagesURL}/FrontendUtilities/delete1.png`}
                  alt=""
                />
              </button>
              )}

              {withTestBtn && (
                <div className={classes['test']}>
                  <span className={classes['text']}>
                    {translate(444, 'Test')}
                  </span>
                  <button
                    className={`btn-effect`}
                    onClick={() => this.showPreviewModal(!showPreview)}
                    title={translate(444, 'Test', true)}
                  >
                    <img
                      src={`${imagesURL}/FlinkMake/arrowright.png`}
                      alt="Test"
                    />
                  </button>
                </div>
              )}
            </>
          )}

          <div className={classes.spacer} />

          <div className={classes.row}>
            {withImageDefaultOpenCheckbox && (
              <div className={classes.imgDefaultOpen}>
                <input
                  type="checkbox"
                  id="activity-image-open-by-default"
                  checked={
                    !wordAgs 
                      ? (versionType 
                          ? editingActivity.data?.languageVersionData?.[versionType]?.activityImageDefaultOpen ?? false
                          : editingActivity.data?.gameData?.activityImageDefaultOpen ?? false)
                      : false
                  }
                  onChange={e => {
                    if (!wordAgs) {
                      if (versionType){
                        changeActivityData(`languageVersionData[${versionType}].activityImageDefaultOpen`, e.target.checked)
                      }
                      else{
                        changeActivityData(`gameData.activityImageDefaultOpen`, e.target.checked)
                      }
                    }
                    else{
                      change('WordAgsForm', `multilanguageWords.[${wordIndex}].wordImage`, e.target.checked ?? '');
                    }                    
                  }}
                />
                <label htmlFor="activity-image-open-by-default">
                  {translate(0, 'Auto-open Activity Image')}
                </label>
              </div>
            )}
            {withSize && (
              <div>
                <span className={classes.formLabel}>Activity Image Size</span>
                <RadioGroup
                  name={versionType ? `languageVersionData[${versionType}].activityImageSize` 
                  : "gameData.activityImageSize"}
                  changeHandler={changeActivityData}
                  current={editingActivity.data}
                  options={[
                    {
                      label: 'Large',
                      value: 'large',
                    },
                    {
                      label: 'Small',
                      value: 'small',
                    },
                  ]}
                />
              </div>
            )}
          </div>

          <PreviewImage
            closeHandler={() => this.showPreviewModal(false)}
            show={showPreview}
            imageSrc={currentSrc}
            size={{ width: '30vmin' }}
            withOverlay
          />
        </div>
      </>
    );
  }
}

AddImageBlock.propTypes = {
  editingActivity: PropTypes.object,
  changeActivityData: PropTypes.func.isRequired,
  pathToProp: PropTypes.string.isRequired,
  fileName: PropTypes.string,
  saveEditingActivity: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  showFilename: PropTypes.bool,
  withTestBtn: PropTypes.bool,
  withOverlay: PropTypes.bool,
  withSize: PropTypes.bool,
  withImageDefaultOpenCheckbox: PropTypes.bool,
  withoutPreview: PropTypes.bool,
  withoutTitle: PropTypes.bool,
  withId: PropTypes.bool,
  versionType: PropTypes.string,
  wordAgs: PropTypes.bool,
  filePath: PropTypes.string,
  onChangeHandler: PropTypes.func,
  wordIndex: PropTypes.number,
  addImagePerks: PropTypes.bool,
  exclusiveImageActions: PropTypes.bool,
};

const mapStateToProps = ({
  status: { translate },
  flinkMake: {
    activity: { editingActivity },
  },
}) => ({
  translate,
  editingActivity,
});

export default compose(
  connect(mapStateToProps, {
    uploading,
    changeActivityData,
    saveEditingActivity,
  })
)(AddImageBlock);
